import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Redirect from './Redirect';
import userService from '../services/userService';
import {
  ACCESS_ROLES,
  routes,
  disableIframes,
  ACCESS_TOKEN_COOKIE,
  PROVIDER_DETAILS_COOKIE,
  CACHE_CONTROL_COOKIE,
} from '../config';
import providerService from '../services/providerService';
import cookieService from '../services/cookieService';
import featureFlagService from '../services/featureFlagService';
import Spinner from './Spinner';
import { exchangeProviderToken } from '../utils/authprovider';
import { fetchCountryConfig } from '../features/countryConfig/countryConfigSlice';
import { countryConfig as staticCountryConfig } from '../data/countryConfig';
import userDetails from '../actions/userDetailsAction';
import { DEFAULT_FLAGS } from '../constants/constants';

const Initializer = () => {
  const pDetails = providerService.getProviderDetails();
  const currentProvider = providerService.getCurrentProvider();
  const hasClinic = Object.keys(currentProvider).length > 0;
  const uDetails = userService.getUserDetails();
  let { role } = uDetails;
  const userCountry = localStorage.getItem('userCountry');
  const userLang = localStorage.getItem('userLang');
  const dispatch = useDispatch();

  const [nextStep, setNextStep] = useState();
  const [nextPage, setNextPage] = useState();
  const [featureFlagsReceived, setFeatureFlagsReceived] =
    useState(false);
  const [providerTokenReceived, setProviderTokenReceived] =
    useState(false);
  const [countryConfigReceived, setCountryConfigReceived] =
    useState(false);

  const chosenProvider = pDetails?.providerId;

  localStorage.setItem('disableIframes', disableIframes());

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setNextStep('home?error=provider');
    }, 30000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const fetchFeatureFlags = async () => {
    const defaultFlags = DEFAULT_FLAGS;
    const serviceEnrolments =
      await featureFlagService.getFeatureFlagConfig(
        pDetails?.providerId,
        uDetails?.professionalId,
        currentProvider?.countryCode,
      );

    const allFlags = {
      featureEnabler: {
        ...defaultFlags.featureEnabler,
        ...serviceEnrolments?.featureEnabler,
      },
    };
    localStorage.setItem('featureFlags', JSON.stringify(allFlags));
    setFeatureFlagsReceived(true);
  };

  useEffect(() => {
    async function exchangePToken() {
      const providerToken = await exchangeProviderToken(
        chosenProvider,
      );
      if (providerToken) {
        // decode provider token and determine user role
        const { providerRole } =
          userService.decodeAccessToken(providerToken);

        if (providerRole) {
          role = providerRole;
          const updatedUserDetails = {
            ...uDetails,
            role,
          };

          // overwrite the user role and update global state
          dispatch(userDetails(updatedUserDetails));
        }

        setNextPage(
          role === ACCESS_ROLES.RESOURCES_AND_TRAINING
            ? routes.dpx.resources
            : routes.dpx.home,
        );

        setProviderTokenReceived(true);
      } else {
        // token unsuccessful
        // the clinic relationship may not exist anymore
        // 1. clear the providers storage
        // 2. Drop cookie to make sure non-cached provider list is retrieved at next login
        // 3. log the user out
        cookieService.removeCookie(PROVIDER_DETAILS_COOKIE);
        cookieService.setCookie(
          CACHE_CONTROL_COOKIE,
          JSON.stringify({
            professionalId: uDetails.professionalId,
            nocache: true,
          }),
        );
        window.location.href = routes.dpx.logout;
      }
    }

    async function getCountryConfig() {
      // get country config and store in local storage
      dispatch(
        fetchCountryConfig(
          hasClinic
            ? currentProvider.address?.countryCode
            : uDetails.countryCode,
        ),
      );
      setCountryConfigReceived(true);
    }

    // get local static country config
    // and store in local storage if found
    function getStaticCountryConfig(country) {
      if (!country) return null;
      const countryConfig = staticCountryConfig.find((c) => {
        return c.countryCode === country;
      });
      if (countryConfig) {
        localStorage.setItem(
          'countryConfig',
          JSON.stringify(countryConfig),
        );
        setCountryConfigReceived(true);
      }
      return countryConfig;
    }

    const accessToken = cookieService.getCookie(ACCESS_TOKEN_COOKIE);
    if (!accessToken) {
      // if token cookie is no longer present, log the user out
      window.location.href = routes.dpx.logout;
    } else {
      if (
        !getStaticCountryConfig(
          hasClinic
            ? currentProvider.address?.countryCode
            : uDetails.countryCode,
        )
      ) {
        getCountryConfig();
      }
      if (hasClinic) {
        exchangePToken();
        fetchFeatureFlags();
      } else {
        // 0 clinic
        localStorage.removeItem('featureFlags');
        setProviderTokenReceived(true);
        setFeatureFlagsReceived(true);
        setNextPage(routes.dpx.resources);
      }
    }
  }, [chosenProvider]);

  useEffect(() => {
    if (
      featureFlagsReceived &&
      providerTokenReceived &&
      countryConfigReceived
    ) {
      setNextStep(nextPage);
    }
  }, [
    featureFlagsReceived,
    providerTokenReceived,
    countryConfigReceived,
  ]);

  if (nextStep) {
    return (
      <Redirect
        to={nextStep}
        country={userCountry}
        lang={userLang}
        refresh
      />
    );
  }
  return <Spinner loading />;
};

export default Initializer;
